import { Alert, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import moment from 'moment';
import { CommonEventValidation, CreateEvent, EditEvent } from 'src/types/Events';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import './appointmentForm.scss';

interface Props {
  event: CreateEvent | EditEvent;
  setEvent: (event: CreateEvent | EditEvent) => void;
  visitTypesList: any;
  subVisitTypesList: any;
  validationObj: CommonEventValidation;
  setValidationObj: (validationObj: CommonEventValidation) => any;
  isNew: (event: CreateEvent | EditEvent) => boolean;
}

export default function EventsForm({
  event,
  setEvent,
  visitTypesList,
  subVisitTypesList,
  validationObj,
  setValidationObj,
  isNew
}: Props) {
  const { userPreferredDateTimeFormat } = usePreferedDateFormat();
  const isNewOrEdit = isNew(event);

  return (
    <Grid container spacing={2} className="event-form">
      <Grid item xs={6}>
        <FormControl variant="standard" fullWidth error={validationObj.visit_type}>
          <InputLabel id="visit-type">
            Visit Type <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            id="visit-type-select-standard"
            value={event.visit_type}
            sx={{ minWidth: '265px' }}
            onChange={(e: any) => {
              setValidationObj({ ...validationObj, visit_type: false });
              setEvent({ ...event, visit_type: e.target.value });
            }}
          >
            {visitTypesList &&
              visitTypesList.map((visit: any) => {
                return (
                  <MenuItem key={visit.id} value={visit.code_list_value}>
                    {visit.code_list_value}
                  </MenuItem>
                );
              })}
          </Select>
          {validationObj.visit_type && <FormHelperText>{'Required!'}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl variant="standard" fullWidth error={validationObj.sub_type}>
          <InputLabel id="sub-visit-type">
            Sub Type <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            id="sub-visit-type-select-standard"
            value={event.sub_type}
            sx={{ minWidth: '265px' }}
            onChange={(e: any) => {
              setValidationObj({ ...validationObj, sub_type: false });
              setEvent({ ...event, sub_type: e.target.value });
            }}
          >
            {subVisitTypesList &&
              subVisitTypesList.map((subVisit: any) => {
                return (
                  <MenuItem key={subVisit.id} value={subVisit.code_list_value}>
                    {subVisit.code_list_value}
                  </MenuItem>
                );
              })}
          </Select>
          {validationObj.sub_type && <FormHelperText>{'Required!'}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl fullWidth>
            <DateTimePicker
              className="modifylabel"
              label="Start Date"
              value={event.start_date !== '' ? event.start_date : null}
              inputFormat={userPreferredDateTimeFormat}
              maxDateTime={dayjs(event.end_date)}
              closeOnSelect
              onChange={(newValue: any) => {
                if (newValue !== null) {
                  setValidationObj({ ...validationObj, start_date: false });
                  setEvent({ ...event, start_date: moment.utc(newValue.$d).format() });
                } else {
                  setEvent({ ...event, start_date: '' });
                }
              }}
              onAccept={(newValue: any) => {
                if (newValue !== null) {
                  setValidationObj({ ...validationObj, start_date: false });
                  setEvent({ ...event, start_date: moment.utc(newValue.$d).format() });
                } else {
                  setEvent({ ...event, start_date: '' });
                }
              }}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  error={validationObj.start_date || event.start_date === 'Invalid date'}
                  helperText={validationObj.start_date && 'Required!'}
                />
              )}
            />
          </FormControl>
        </LocalizationProvider>
      </Grid>
      <Grid item xs={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl fullWidth>
            <DateTimePicker
              className="modifylabel"
              label="End Date"
              value={event.end_date !== '' ? event.end_date : null}
              inputFormat={userPreferredDateTimeFormat}
              minDateTime={dayjs(event.start_date)}
              closeOnSelect
              onAccept={(newValue: any) => {
                if (newValue !== null) {
                  setValidationObj({ ...validationObj, end_date: false });
                  setEvent({ ...event, end_date: moment.utc(newValue.$d).format() });
                } else {
                  setEvent({ ...event, end_date: '' });
                }
              }}
              onChange={(newValue: any) => {
                if (newValue !== null) {
                  setValidationObj({ ...validationObj, end_date: false });
                  setEvent({ ...event, end_date: moment.utc(newValue.$d).format() });
                } else {
                  setEvent({ ...event, end_date: '' });
                }
              }}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  error={validationObj.end_date || event.end_date === 'Invalid date'}
                  helperText={validationObj.end_date && 'Required!'}
                />
              )}
            />
          </FormControl>
        </LocalizationProvider>
      </Grid>
      {event.visit_type === 'Interim Monitoring Visit' && (
        <Grid item xs={12} pt={0.5}>
          <Alert severity="info">
            {isNewOrEdit
              ? 'A new Interim Monitoring Report will be created and must be completed'
              : 'Interim Monitoring Report will be updated and must be completed'}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
}
